/**
 * Author: Housefolios
 * Created: 2025-01-27 16:41:43
 * Modified by: David Helmick
 * Modified: 2025-01-27 16:59:48
 * Description: The step by step process of uploading contacts from Excel files with multiple tabs
 */

import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'
import { wizardStepIconSx } from '@/theme/skinSx'
import withSession from '@/housefolios-components/Session/withSession'
import Alert from '@mui/material/Alert'
import { useDropzone } from 'react-dropzone'
import Autocomplete from '@mui/material/Autocomplete'

// Centralized lodash imports - much cleaner!
import {
  map,
  forEach,
  toLower,
  isEmpty,
  find,
  set,
  compact,
  clone,
  filter,
  merge,
  cloneDeep,
  includes,
  isArray,
  trim,
  toString,
  keys} from '@/utils/lodash'

import Papa from 'papaparse'
import { loadWorkbook, sheetToAoA } from '@/utils/excel'

import {
  Grid, Box, Button, Card, CardContent, Chip, FormControl,  InputLabel, MenuItem, Paper, Select, Stepper, Step, StepLabel, Table, Typography, Checkbox } from '@mui/material'

import Check from '@mui/icons-material/Check'
import CheckIcon from '@mui/icons-material/Check'
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CloudUploadTwoToneIcon from '@mui/icons-material/CloudUploadTwoTone'
import FolderIcon from '@mui/icons-material/Folder'
import StepConnector from '@mui/material/StepConnector'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'

import { enqueueSnackbar } from 'notistack'
import { ArrowForward, Close } from '@mui/icons-material'
import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'

// GraphQL mutation for bulk contact creation
const CREATE_CONTACTS_BULK = gql`
  mutation CreateContactsBulk($contacts: [ContactInput!]!) {
    createContactsBulk(contacts: $contacts) {
      _id
      name
      types
      email
      phone
      company
      notes
      address
      city
      state
      area
      ranking
      image {
        _id
        url
        url_small
      }
      optionalFields
      createdAt
      updatedAt
    }
  }
`

// Contact column definitions
const contactColumns = [
  {
    name: 'name',
    label: 'Name',
    required: true,
    alts: ['name', 'full name', 'contact name', 'person', 'contact']},
  {
    name: 'email',
    label: 'Email',
    required: false,
    alts: ['email', 'email address', 'e-mail', 'e-mail address']},
  {
    name: 'phone',
    label: 'Phone',
    required: false,
    alts: [
      'phone',
      'phone number',
      'telephone',
      'tel',
      'mobile',
      'cell',
    ]},
  {
    name: 'company',
    label: 'Company',
    required: false,
    alts: ['company', 'business', 'organization', 'firm', 'agency']},
  {
    name: 'address',
    label: 'Address',
    required: false,
    alts: ['address', 'street', 'street address', 'location']},
  {
    name: 'city',
    label: 'City',
    required: false,
    alts: ['city', 'town', 'municipality']},
  {
    name: 'state',
    label: 'State',
    required: false,
    alts: ['state', 'province', 'region']},
  {
    name: 'area',
    label: 'Area',
    required: false,
    alts: ['area', 'county', 'district', 'zone']},
  {
    name: 'notes',
    label: 'Notes',
    required: false,
    alts: ['notes', 'comments', 'description', 'details', 'remarks']},
]

// Tab to contact type mapping
const tabToContactType = {
  contractors: 'Contractor',
  inspectors: 'Home Inspector',
  realtors: 'Agent',
  lenders: 'Hard Money Lender',
  insurance: 'Insurance Agent',
  title: 'Title Company',
  investors: 'Investor',
  buyers: 'Buyer',
  sellers: 'Seller',
  'property managers': 'Property Manager',
  appraisers: 'Appraiser',
  attorneys: 'Attorney',
  accountants: 'Accountant',
  plumbers: 'Plumber',
  electricians: 'Electrician',
  roofers: 'Roofer',
  painters: 'Painter',
  cleaners: 'Cleaner',
  landscapers: 'Landscaper',
  'general contractors': 'Contractor'}

function StepIcon(props) {
  const { active, completed, icon } = props

  return (
    <Box sx={wizardStepIconSx({ active, completed })}>
      {completed ? <CheckIcon /> : icon}
    </Box>
  )
}

StepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
  icon: PropTypes.node}

function getSteps() {
  return ['Upload File', 'Map Columns', 'Review & Import']
}

function UploadContactsStepperComponent(props) {
  const [activeStep, setActiveStep] = useState(0)
  const steps = getSteps()

  const handleNext = () => {
    setActiveStep((prevActiveStep) => {
      const next = prevActiveStep + 1
      props?.onActiveStepChange && props.onActiveStepChange(next)
      return next
    })
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => {
      const next = prevActiveStep - 1
      props?.onActiveStepChange && props.onActiveStepChange(next)
      return next
    })
  }

  const [createContactsBulk] = useMutation(CREATE_CONTACTS_BULK)

  // Inform parent of initial step on mount
  useEffect(() => {
    props?.onActiveStepChange && props.onActiveStepChange(activeStep)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [fileData, setFileData] = useState({})
  const [mappedColumns, setMappedColumns] = useState({})
  const [contactTypes, setContactTypes] = useState({})
  const [processedContacts, setProcessedContacts] = useState([])
  const [currentTabIndex, setCurrentTabIndex] = useState(0)
  const [tabNames, setTabNames] = useState([])
  const [isImporting, setIsImporting] = useState(false)
  const [selectedTabs, setSelectedTabs] = useState(new Set())

  // Reset currentTabIndex when selected tabs change
  useEffect(() => {
    const selectedTabNames = tabNames.filter((tabName) =>
      selectedTabs.has(tabName),
    )
    if (
      currentTabIndex >= selectedTabNames.length &&
      selectedTabNames.length > 0
    ) {
      setCurrentTabIndex(0)
    }
  }, [selectedTabs, tabNames, currentTabIndex])

  // Process contacts when entering review step
  useEffect(() => {
    if (activeStep === 2 && processedContacts.length === 0) {
      processContactsForPreview()
    }
  }, [activeStep, processedContacts.length])

  const { acceptedFiles, isDragActive, getRootProps, getInputProps } =
    useDropzone({
      multiple: false,
      accept: {
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          ['.xlsx'],
        'text/csv': ['.csv'],
        'application/csv': ['.csv'],
        'text/x-comma-separated-values': ['.csv'],
        'text/comma-separated-values': ['.csv']},
      onDrop: async (acceptedFiles) => {
        const file = acceptedFiles[0]
        if (!file) return

        const isExcel = /\.xlsx$/i.test(file.name)
        const isCSV = /\.csv$/i.test(file.name)

        if (isExcel) {
          try {
            const workbook = await loadWorkbook(await file.arrayBuffer())
            const fileDataObj = {}
            const contactTypesObj = {}
            const mappedColumnsObj = {}

            workbook.worksheets.forEach((worksheet) => {
              const sheetName = worksheet.name
              const jsonData = sheetToAoA(worksheet)

              if (jsonData.length > 1) {
                // Has headers and at least one data row
                const headers = jsonData[0]
                const dataRows = jsonData.slice(1)

                // Convert to object format
                const processedData = dataRows
                  .map((row) => {
                    const obj = {}
                    headers.forEach((header, index) => {
                      if (header && row[index] !== undefined) {
                        obj[header] = row[index]
                      }
                    })
                    return obj
                  })
                  .filter((row) => Object.keys(row).length > 0) // Remove empty rows

                if (processedData.length > 0) {
                  fileDataObj[sheetName] = processedData

                  // Try to auto-detect contact type from sheet name
                  const sheetNameLower = sheetName.toLowerCase()
                  let detectedType = 'Agent' // Default

                  // Auto-detect contact type from sheet name
                  if (tabToContactType[sheetNameLower]) {
                    detectedType = tabToContactType[sheetNameLower]
                  } else if (sheetNameLower.includes('contractor')) {
                    detectedType = 'Contractor'
                  } else if (sheetNameLower.includes('inspector')) {
                    detectedType = 'Home Inspector'
                  } else if (
                    sheetNameLower.includes('realtor') ||
                    sheetNameLower.includes('agent')
                  ) {
                    detectedType = 'Agent'
                  } else if (
                    sheetNameLower.includes('hard money') ||
                    sheetNameLower.includes('hardmoney')
                  ) {
                    detectedType = 'Hard Money Lender'
                  } else if (sheetNameLower.includes('lender')) {
                    detectedType = 'Lender'
                  } else if (sheetNameLower.includes('insurance')) {
                    detectedType = 'Insurance Agent'
                  } else if (sheetNameLower.includes('title')) {
                    detectedType = 'Title Company'
                  } else if (sheetNameLower.includes('investor')) {
                    detectedType = 'Investor'
                  } else if (sheetNameLower.includes('buyer')) {
                    detectedType = 'Buyer'
                  } else if (sheetNameLower.includes('seller')) {
                    detectedType = 'Seller'
                  } else if (sheetNameLower.includes('property manager')) {
                    detectedType = 'Property Manager'
                  } else if (sheetNameLower.includes('appraiser')) {
                    detectedType = 'Appraiser'
                  } else if (
                    sheetNameLower.includes('gap partner') ||
                    sheetNameLower.includes('gappartner')
                  ) {
                    detectedType = 'Gap Partner'
                  } else {
                    detectedType = 'Other' // Default for unrecognized types
                  }

                  contactTypesObj[sheetName] = detectedType
                  mappedColumnsObj[sheetName] = {}
                }
              }
            })

            if (Object.keys(fileDataObj).length > 0) {
              setFileData(fileDataObj)
              setTabNames(Object.keys(fileDataObj))
              setMappedColumns(mappedColumnsObj)
              setContactTypes(contactTypesObj)
              setCurrentTabIndex(0)
              setSelectedTabs(new Set(Object.keys(fileDataObj))) // Select all tabs by default
            } else {
              console.error('No valid data found in Excel file')
            }
          } catch (error) {
            console.error('Error parsing Excel file:', error)
            enqueueSnackbar(
              'Unable to read Excel file. Please upload a .xlsx file.',
              { variant: 'error' },
            )
          }
        } else if (isCSV) {
          // Handle CSV files - treat as single tab
          const reader = new FileReader()
          reader.onload = function () {
            Papa.parse(reader.result, {
              header: true,
              skipEmptyLines: true,
              complete: (result) => {
                const data = result.data
                if (data.length > 0) {
                  setFileData({ main: data  })
                  setTabNames(['main'])
                  setMappedColumns({ main: {} })
                  setContactTypes({ main: 'Agent'  }) // Default type
                  setCurrentTabIndex(0)
                  setSelectedTabs(new Set(['main'])) // Select the single tab
                }
              }})
          }
          reader.readAsText(file)
        } else {
          enqueueSnackbar(
            'Unsupported file type. Please upload .xlsx or .csv',
            {
              variant: 'error'},
          )
        }
      }})

  const handleColumnMapping = (tabName, columnName, mappedField) => {
    setMappedColumns((prev) => ({
      ...prev,
      [tabName]: {
        ...prev[tabName],
        [columnName]: mappedField}}))
  }

  const handleContactTypeChange = (tabName, contactType) => {
    setContactTypes((prev) => ({
      ...prev,
      [tabName]: contactType,
    }))
  }

  const handleNextTab = () => {
    const selectedTabNames = tabNames.filter((tabName) =>
      selectedTabs.has(tabName),
    )

    if (currentTabIndex < selectedTabNames.length - 1) {
      setCurrentTabIndex(currentTabIndex + 1)
    } else {
      // All tabs processed, move to review step (not process contacts yet)
      handleNext()
    }
  }

  const handlePreviousTab = () => {
    if (currentTabIndex > 0) {
      setCurrentTabIndex(currentTabIndex - 1)
    }
  }

  const toggleTabSelection = (tabName) => {
    setSelectedTabs((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(tabName)) {
        newSet.delete(tabName)
      } else {
        newSet.add(tabName)
      }
      return newSet
    })
    // Reset currentTabIndex when selection changes to avoid out-of-bounds issues
    setCurrentTabIndex(0)
  }

  const selectAllTabs = () => {
    setSelectedTabs(new Set(tabNames))
    setCurrentTabIndex(0)
  }

  const deselectAllTabs = () => {
    setSelectedTabs(new Set())
    setCurrentTabIndex(0)
  }

  const handleImportContacts = async () => {
    await processContacts()
  }

  const processContactsForPreview = () => {
    const contacts = []

    // Only process selected tabs
    const tabsToProcess = tabNames.filter((tabName) =>
      selectedTabs.has(tabName),
    )

    tabsToProcess.forEach((tabName) => {
      const tabData = fileData[tabName]
      const tabMappings = mappedColumns[tabName] || {}
      const contactType = contactTypes[tabName] || 'Agent'

      if (tabData && Array.isArray(tabData)) {
        tabData.forEach((row, rowIndex) => {
          const contact = {
            name: row[tabMappings.name] || '',
            email: row[tabMappings.email] || '',
            phone: row[tabMappings.phone] || '',
            company: row[tabMappings.company] || '',
            address: row[tabMappings.address] || '',
            city: row[tabMappings.city] || '',
            state: row[tabMappings.state] || '',
            area: row[tabMappings.area] || '',
            notes: row[tabMappings.notes] || '',
            types: [contactType],
            ranking: null,
            optionalFields: {},
            // sourceTab: tabName, // Remove this - not part of ContactInput schema
          }

          if (contact.name.trim()) {
            contacts.push(contact)
          }
        })
      }
    })

    setProcessedContacts(contacts)
  }

  const processContacts = async () => {
    setIsImporting(true)
    const contacts = []

    // Only process selected tabs
    const tabsToProcess = tabNames.filter((tabName) =>
      selectedTabs.has(tabName),
    )

    tabsToProcess.forEach((tabName) => {
      const tabData = fileData[tabName]
      const tabMappings = mappedColumns[tabName] || {}
      const contactType = contactTypes[tabName] || 'Agent'

      if (tabData && Array.isArray(tabData)) {
        tabData.forEach((row, rowIndex) => {
          const contact = {
            name: row[tabMappings.name] || '',
            email: row[tabMappings.email] || '',
            phone: row[tabMappings.phone] || '',
            company: row[tabMappings.company] || '',
            address: row[tabMappings.address] || '',
            city: row[tabMappings.city] || '',
            state: row[tabMappings.state] || '',
            area: row[tabMappings.area] || '',
            notes: row[tabMappings.notes] || '',
            types: [contactType],
            ranking: null,
            optionalFields: {},
            // sourceTab: tabName, // Remove this - not part of ContactInput schema
          }

          if (contact.name.trim()) {
            contacts.push(contact)
          }
        })
      }
    })

    setProcessedContacts(contacts)

    try {
      const { data } = await createContactsBulk({
        variables: { contacts }})
      enqueueSnackbar('Contacts imported successfully!', {
        variant: 'success'})
      setIsImporting(false)

      // Close the dialog and refresh the contacts list
      if (props.handleCloseUploadContacts) {
        props.handleCloseUploadContacts()
      }

      // Trigger a refetch of the contacts list instead of page reload
      if (props.onContactsImported) {
        props.onContactsImported()
      }
    } catch (error) {
      console.error('Error importing contacts:', error)
      enqueueSnackbar('Failed to import contacts.', {
        variant: 'error'})
      setIsImporting(false)
    }
  }

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div>
            <Typography variant="h6" gutterBottom>
              Upload Contact File
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                marginBottom: "16px"
              }}
            >
              Upload an Excel file with multiple tabs or a CSV file.
              Each tab will be mapped to a contact type.
            </Typography>

            <div
              {...getRootProps()}
              style={{
                border: '2px dashed #ccc',
                borderRadius: '8px',
                padding: '40px',
                textAlign: 'center',
                backgroundColor: isDragActive ? '#f0f8ff' : '#fafafa',
                cursor: 'pointer'}}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon
                style={{ fontSize: 48, color: '#666' }}
              />
              <Typography variant="h6" style={{ marginTop: 16 }}>
                {isDragActive
                  ? 'Drop the file here'
                  : 'Drag & drop a file here, or click to select'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Supports Excel (.xlsx) and CSV files
              </Typography>
            </div>

            {Object.keys(fileData).length > 0 && (
              <Alert severity="success" style={{ marginTop: 16 }}>
                File uploaded successfully! Found {tabNames.length}{' '}
                tab(s): {tabNames.join(', ')}
              </Alert>
            )}

            {/* Tab Selection Interface */}
            {tabNames.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Select Tabs to Import
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      marginBottom: "16px"
                    }}
                  >
                    Choose which tabs you want to import. You can
                    configure each tab's contact type and column
                    mapping in the next step.
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Button
                      size="small"
                      onClick={selectAllTabs}
                      sx={{ mr: 1 }}
                    >
                      Select All
                    </Button>
                    <Button size="small" onClick={deselectAllTabs}>
                      Deselect All
                    </Button>
                  </Box>

                  <Grid container spacing={2}>
                    {tabNames.map((tabName, index) => {
                      const isSelected = selectedTabs.has(tabName)
                      const tabData = fileData[tabName]
                      const contactCount = tabData
                        ? tabData.length
                        : 0
                      const contactType =
                        contactTypes[tabName] || 'Agent'

                      return (
                        <Grid size={{xs: 12, sm: 6, md: 4}}
                          key={tabName}
                        >
                          <Card
                            sx={{
                              border: isSelected
                                ? '2px solid #1976d2'
                                : '1px solid #e0e0e0',
                              backgroundColor: isSelected
                                ? '#f3f8ff'
                                : 'white',
                              cursor: 'pointer',
                              '&:hover': {
                                borderColor: isSelected
                                  ? '#1976d2'
                                  : '#1976d2',
                                backgroundColor: isSelected
                                  ? '#f3f8ff'
                                  : '#fafafa'}}}
                            onClick={() =>
                              toggleTabSelection(tabName)
                            }
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 1}}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() =>
                                    toggleTabSelection(tabName)
                                  }
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {tabName}
                                </Typography>
                              </Box>

                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ mb: 1 }}
                              >
                                {contactCount} contacts
                              </Typography>

                              <Chip
                                label={contactType}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      )
                    })}
                  </Grid>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    {selectedTabs.size} of {tabNames.length} tabs
                    selected for import
                  </Alert>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 1:
        if (tabNames.length === 0) {
          return (
            <div>
              <Typography variant="h6" gutterBottom>
                No tabs found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Please upload a file with data to continue.
              </Typography>
            </div>
          )
        }

        if (selectedTabs.size === 0) {
          return (
            <div>
              <Typography variant="h6" gutterBottom>
                No tabs selected
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Please select at least one tab to import in the
                previous step.
              </Typography>
            </div>
          )
        }

        // Get only selected tabs for processing
        const selectedTabNames = tabNames.filter((tabName) =>
          selectedTabs.has(tabName),
        )
        const currentTabName = selectedTabNames[currentTabIndex]
        const tabData = fileData[currentTabName]
        const tabColumns =
          tabData && tabData.length > 0 ? Object.keys(tabData[0]) : []
        const tabMappings = mappedColumns[currentTabName] || {}
        const contactType = contactTypes[currentTabName] || 'Agent'

        return (
          <div>
            <Typography variant="h6" gutterBottom>
              Map Columns - Tab {currentTabIndex + 1} of{' '}
              {selectedTabNames.length}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                marginBottom: "16px"
              }}
            >
              Configure the contact type and map columns for "
              {currentTabName}" tab.
            </Typography>

            {/* Tab Navigation */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Selected Tabs:{' '}
                {selectedTabNames.map((tab, index) => (
                  <Chip
                    key={tab}
                    label={tab}
                    color={
                      index === currentTabIndex
                        ? 'primary'
                        : 'default'
                    }
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Typography>
            </Box>

            <Card style={{ marginBottom: 16 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Tab: {currentTabName}
                </Typography>

                <FormControl fullWidth style={{ marginBottom: 16 }}>
                  <InputLabel>Contact Type</InputLabel>
                  <Select
                    value={contactType}
                    onChange={(e) =>
                      handleContactTypeChange(
                        currentTabName,
                        e.target.value,
                      )
                    }
                    label="Contact Type"
                  >
                    <MenuItem value="Agent">Agent</MenuItem>
                    <MenuItem value="Appraiser">Appraiser</MenuItem>
                    <MenuItem value="Buyer">Buyer</MenuItem>
                    <MenuItem value="Contractor">Contractor</MenuItem>
                    <MenuItem value="Gap Partner">
                      Gap Partner
                    </MenuItem>
                    <MenuItem value="Hard Money Lender">
                      Hard Money Lender
                    </MenuItem>
                    <MenuItem value="Home Inspector">
                      Home Inspector
                    </MenuItem>
                    <MenuItem value="Insurance Agent">
                      Insurance Agent
                    </MenuItem>
                    <MenuItem value="Investor">Investor</MenuItem>
                    <MenuItem value="Lender">Lender</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                    <MenuItem value="Property Manager">
                      Property Manager
                    </MenuItem>
                    <MenuItem value="Seller">Seller</MenuItem>
                    <MenuItem value="Title Company">
                      Title Company
                    </MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="subtitle2" gutterBottom>
                  Available columns: {tabColumns.join(', ')}
                </Typography>

                <Grid container spacing={2}>
                  {contactColumns.map((column) => (
                    <Grid size={{xs: 12, sm: 6}} key={column.name}>
                      <FormControl fullWidth>
                        <InputLabel>{column.label}</InputLabel>
                        <Select
                          value={tabMappings[column.name] || ''}
                          onChange={(e) =>
                            handleColumnMapping(
                              currentTabName,
                              column.name,
                              e.target.value,
                            )
                          }
                          label={column.label}
                        >
                          <MenuItem value="">
                            <em>Not mapped</em>
                          </MenuItem>
                          {tabColumns.map((col) => (
                            <MenuItem key={col} value={col}>
                              {col}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  ))}
                </Grid>

                {/* Tab Navigation Buttons */}
                <Box
                  sx={{
                    mt: 3,
                    display: 'flex',
                    justifyContent: 'space-between'}}
                >
                  <Button
                    variant="outlined"
                    onClick={handlePreviousTab}
                    disabled={currentTabIndex === 0}
                  >
                    Previous Tab
                  </Button>
                  <Button variant="contained" onClick={handleNextTab}>
                    {currentTabIndex === selectedTabNames.length - 1
                      ? 'Review & Import'
                      : 'Next Tab'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </div>
        );

      case 2:
        return (
          <div>
            <Typography variant="h6" gutterBottom>
              Review & Import
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                marginBottom: "16px"
              }}
            >
              Review the contacts that will be imported from all tabs.
            </Typography>

            {/* Summary by Tab */}
            {tabNames
              .filter((tabName) => selectedTabs.has(tabName))
              .map((tabName) => {
                const tabData = fileData[tabName]
                const tabMappings = mappedColumns[tabName] || {}
                const contactType = contactTypes[tabName] || 'Agent'
                const tabContacts =
                  tabData && Array.isArray(tabData)
                    ? tabData.filter((row) =>
                        row[tabMappings.name]?.trim(),
                      ).length
                    : 0

                return (
                  <Card key={tabName} sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        {tabName} → {contactType}: {tabContacts}{' '}
                        contacts
                      </Typography>
                    </CardContent>
                  </Card>
                )
              })}

            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Company</th>
                </tr>
              </thead>
              <tbody>
                {processedContacts
                  .slice(0, 10)
                  .map((contact, index) => (
                    <tr key={index}>
                      <td>{contact.name}</td>
                      <td>
                        <Chip label={contact.types[0]} size="small" />
                      </td>
                      <td>{contact.email || 'No Email'}</td>
                      <td>{contact.phone || 'No Phone'}</td>
                      <td>{contact.company || 'No Company'}</td>
                    </tr>
                  ))}
              </tbody>
            </Table>

            {processedContacts.length > 10 && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 8 }}
              >
                ... and {processedContacts.length - 10} more contacts
              </Typography>
            )}

            <Box style={{ marginTop: 16 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleImportContacts}
                disabled={
                  processedContacts.length === 0 || isImporting
                }
              >
                {isImporting ? (
                  <PacmanLoader color="#fff" size={20} />
                ) : (
                  `Import ${processedContacts.length} Contacts`
                )}
              </Button>
            </Box>
          </div>
        );

      default:
        return null
    }
  }

  return (
    <div>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <div style={{ marginTop: 32 }}>
        {renderStepContent(activeStep)}
      </div>

      <div
        style={{
          marginTop: 32,
          display: 'flex',
          justifyContent:
            activeStep > 0 ? 'space-between' : 'flex-end'}}
      >
        {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}
        {activeStep !== 2 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={
              activeStep === steps.length - 1 ||
              selectedTabs.size === 0
            }
          >
            Next
          </Button>
        )}
      </div>
    </div>
  )
}

export default withSession(UploadContactsStepperComponent)
